import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import {
  ApiButton,
  Box,
  ContentTemplate,
  ErrorInfo,
  SectionHeader,
  Typography,
} from 'web-library';
import dayjs from 'dayjs';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import { SearchDate } from '../../../models';
import DownloadAuditProgressModal from '../../../components/modals/DownloadAuditProgressModal';
import { useAccessArrangementReport } from '../logic/useAccessArrangementReport';
import { useGetCheckAuthorityExist } from '../../../api-refactored/user/useGetCheckAuthorityExist';
import {DateInput} from "../logic/useTransactionLog";
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

const useStyles = makeStyles()((theme) =>
({
  warningText: {
    width: '100%',
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  formElementContainer: {
    marginBottom: theme.spacing(2),
    width: '30%',
    marginRight: '5%',
    display: 'inline-block',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  singleFormElementContainer: {
    width: '50%',
  },
  errorContainer: {
    display: 'flex',
  },
  label: {
    color: theme.palette.text.secondary,
  },
  submitButtonContainer: {
    width: '100%',
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
}));

const AccessArrangementReportDetails = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [, setIsError] = useState(true);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [generateTouched, setGenerateTouched] = useState(false);
  const [, setInitData] = useState<DateInput>({
    startDate: '',
    endDate: '',
  });

  const {
    form,
    submitGenerate,
    fileDownloadStatus,
    onDialogClose,
    dialogState,
    process,
  } = useAccessArrangementReport();

  const { handleSubmit, control, formState: { errors }, watch, trigger, setValue } = form;

  const watchStartDate = watch('startDate');
  const watchEndDate = watch('endDate');

  useEffect(() => {
    if ((watchStartDate && generateTouched) || (watchEndDate && generateTouched)) {
      trigger('endDate');
      // trigger('custom');
      trigger('startDate');
    }
  }, [watchStartDate, trigger, generateTouched, watchEndDate]);

  const checkStartDate = () => {
    if (startDate && endDate) {
      if (dayjs(endDate).diff(startDate, 'day') < 0) {
        form.setError('endDate', {
          type: 'custom',
          message: 'Application End Date should be later than Application Start Date',
        });
        setIsError(true);
        resetInitdata();
        return false;
      }
      if (dayjs(endDate).diff(startDate, 'month', true) > parseFloat('12.0')) {
        form.setError('root.custom', {
          type: 'manual',
          message: 'Date range should not be more than 12 months.',
        });
        setIsError(true);
        resetInitdata();
        return false;
      }
    }
    setIsError(false);
    return true;
  };

  /**
   * To reset the init date range when date range check failed.
   * To avoid children component getting invalid date range.
   */
  const resetInitdata = () => {
    setInitData({
      startDate: '',
      endDate: '',
    });
  };

  const checkDateRangeHandler = (formDate: SearchDate) => {
    // set init data only if date range is valid, to avoid unnecessary search API triggered
    const checkResult = checkStartDate();
    if (checkResult) {
      setInitData({
        startDate: formDate.startDate,
        endDate: formDate.endDate,
      });
      submitGenerate({ ...formDate });
    }
  };

  /**
   * On page initialize, set generateTouched to true only if there is no error
   * Once set to true, isError state will be used to track API call errors
   */
  const onGenerateClickedHandler = async () => {
    if (Object.keys(errors).length === 0 && errors.constructor === Object) {
      setGenerateTouched(true);
    }
  };
  useGetCheckAuthorityExist({
    authorityName: AUTHORITY_NAMES.CP_ADMIN_ACCESS_ARRANGEMENT_REPORT_VIEW,
  });

  return (
    <ContentTemplate type="full">
      <SectionHeader title={t('admin->>AccessArrangementReport.ReportDetail')} />
      <form noValidate onSubmit={handleSubmit(checkDateRangeHandler)}>
        <Box className={classes.formElementContainer}>
          <ControlledDatePicker
            label={t('admin->>AccessArrangementReport.AccessArrangementStartDate')}
            onChange={(date) => {
              setStartDate(date || '');
              setValue('startDate', date || '');
            }}
            control={control}
            name="startDate"
            disableFuture
            error={!!errors.startDate}
            helperText={errors?.startDate?.message?.toString()}
            inputVariant="filled"
            autoOk
            isRequired
            maxDate={dayjs()}
          />
        </Box>

        <Box className={classes.formElementContainer}>
          <ControlledDatePicker
            label={t('admin->>AccessArrangementReport.AccessArrangementEndDate')}
            // @ts-ignore
            onChange={(date: any) => {
              setEndDate(date);
              setValue('endDate', date ? date.toString() : '');
            }}
            control={control}
            name="endDate"
            maxDate={dayjs()}
            disableFuture
            error={!!errors.endDate}
            helperText={errors?.endDate?.message?.toString()}
            inputVariant="filled"
            autoOk
            isRequired
          />
        </Box>

        <Box className={classes.errorContainer}>
          <Typography className={classes.label} variant="subtitle2">
            {t('admin->>AccessArrangementReport.DateRangeMonths', { noOfMonths: 12 })}
          </Typography>
        </Box>
        {errors?.root?.custom?.message !== '' && (
          <Box
            className={[classes.formElementContainer, classes.singleFormElementContainer].join(' ')}
          >
            <ErrorInfo label={errors?.root?.custom?.message?.toString()} />
          </Box>
        )}

        <Box className={classes.submitButtonContainer}>
          <ApiButton status={process} type="submit" onClick={onGenerateClickedHandler}>
            {t('admin->>AccessArrangementReport.Generate')}
          </ApiButton>
        </Box>
      </form>

      <DownloadAuditProgressModal
        fileDownloadStatus={fileDownloadStatus}
        modalState={dialogState}
        onDialogClose={onDialogClose}
      />
    </ContentTemplate>
  );
};

export default AccessArrangementReportDetails;
